import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal.mjs';
import ClosingX from './ClosingX.jsx';
import Chip from './common/Chip.jsx';
import EmptyState from './common/EmptyState.jsx';
import {
    listProjects,
    createProject,
    addFilesToProject,
    removeFilesFromProject,
} from '../services/ProjectService.mjs';
import { notify, reportError } from '../notifications/errorBus';
import '../css/BulkActions.css';

const SKIP = { skipGlobalErrorToast: true };

/**
 * Add or remove the selected files from a project.
 *
 * Unlike the bulk TAG modal, this does NOT loop per file: the membership routes
 * take the whole id list in one request (up to 5000) and report which ids they
 * skipped. So one click is one round trip regardless of selection size, and
 * there's no partial-progress state to manage.
 */
const BulkProjectModal = ({ files, onClose, onApplied }) => {
    const [tab, setTab] = useState('add');
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [newName, setNewName] = useState('');

    useEffect(() => {
        listProjects()
            .then((list) => setProjects(Array.isArray(list) ? list : []))
            .catch(() => setProjects([]))
            .finally(() => setLoading(false));
    }, []);

    const fileIds = useMemo(() => files.map((f) => f.id), [files]);

    // Which projects the selection already touches, and how many files each
    // covers — so "remove" only offers projects that are actually in play, and
    // "add" can show a partial-coverage hint.
    const membership = useMemo(() => {
        const counts = new Map();
        for (const file of files) {
            for (const id of (file.metadata?.projectIds || [])) {
                counts.set(id, (counts.get(id) || 0) + 1);
            }
        }
        return counts;
    }, [files]);

    const byId = useMemo(
        () => Object.fromEntries(projects.map((p) => [p.id, p])),
        [projects]
    );

    const present = useMemo(
        () => [...membership.entries()]
            .map(([id, count]) => ({ id, count, name: byId[id]?.name || id, color: byId[id]?.color }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        [membership, byId]
    );

    const finish = (verb, { changed, skipped }) => {
        if (changed > 0) {
            notify({
                type: 'success',
                title: `Project membership ${verb}`,
                message: `${changed} file${changed === 1 ? '' : 's'} ${verb}.`,
            });
        }
        if (skipped?.length) {
            // Skipped means the id no longer resolves in this workspace — almost
            // always a stale selection after someone else deleted a file.
            reportError(
                `${skipped.length} file${skipped.length === 1 ? '' : 's'} could not be updated — they may have been deleted.`,
                { title: 'Some files were skipped' }
            );
        }
        onApplied?.();
        onClose?.();
    };

    const applyAdd = async (projectId) => {
        if (!projectId || busy) return;
        setBusy(true);
        try {
            finish('added', await addFilesToProject(projectId, fileIds, SKIP));
        } catch (err) {
            reportError(err, { title: 'Could not add files to the project' });
        } finally {
            setBusy(false);
        }
    };

    const applyRemove = async (projectId) => {
        if (!projectId || busy) return;
        setBusy(true);
        try {
            finish('removed', await removeFilesFromProject(projectId, fileIds, SKIP));
        } catch (err) {
            reportError(err, { title: 'Could not remove files from the project' });
        } finally {
            setBusy(false);
        }
    };

    const createAndAdd = async (e) => {
        e.preventDefault();
        const name = newName.trim();
        if (!name || busy) return;
        setBusy(true);
        try {
            const project = await createProject({ name }, SKIP);
            finish('added', await addFilesToProject(project.id, fileIds, SKIP));
        } catch (err) {
            // A duplicate name is a 400 with a readable message from the backend.
            reportError(err, { title: 'Could not create the project' });
            setBusy(false);
        }
    };

    return (
        <Modal onClose={onClose}>
            <div className="bulk-modal">
                <div className="bulk-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h2>Projects</h2>
                        <p>{files.length} file(s) selected</p>
                    </div>
                    <ClosingX onClick={onClose} />
                </div>

                <div className="bulk-tabs">
                    <button
                        className={`bulk-tab ${tab === 'add' ? 'active' : ''}`}
                        onClick={() => setTab('add')}
                    >
                        Add
                    </button>
                    <button
                        className={`bulk-tab ${tab === 'remove' ? 'active' : ''}`}
                        onClick={() => setTab('remove')}
                    >
                        Remove
                    </button>
                </div>

                {tab === 'add' && (
                    <div className="bulk-section">
                        {loading ? (
                            <p className="bulk-field-hint">Loading projects…</p>
                        ) : projects.length === 0 ? (
                            <EmptyState
                                compact
                                title="No projects yet"
                                description="Create one below to group these files."
                            />
                        ) : (
                            <div className="bulk-pill-list">
                                {projects.map((project) => {
                                    const count = membership.get(project.id) || 0;
                                    const all = count === files.length;
                                    return (
                                        <button
                                            key={project.id}
                                            className="bulk-pill-btn"
                                            onClick={() => applyAdd(project.id)}
                                            disabled={busy || all}
                                            title={all
                                                ? 'Every selected file is already in this project'
                                                : `Add ${files.length} file(s) to ${project.name}`}
                                        >
                                            <Chip size="sm" color={project.color}>
                                                {project.name}
                                                {count > 0 && !all ? ` (${count}/${files.length})` : ''}
                                                {all ? ' ✓' : ''}
                                            </Chip>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        <form className="bulk-create" onSubmit={createAndAdd}>
                            <input
                                className="bulk-create-input"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder="New project name…"
                                maxLength={128}
                                disabled={busy}
                                aria-label="New project name"
                            />
                            <button
                                type="submit"
                                className="qd-btn qd-btn--primary qd-btn--sm"
                                disabled={busy || !newName.trim()}
                            >
                                Create &amp; add
                            </button>
                        </form>
                    </div>
                )}

                {tab === 'remove' && (
                    <div className="bulk-section">
                        {present.length === 0 ? (
                            <EmptyState
                                compact
                                title="Not in any project"
                                description="None of the selected files belong to a project."
                            />
                        ) : (
                            <div className="bulk-pill-list">
                                {present.map((project) => (
                                    <button
                                        key={project.id}
                                        className="bulk-pill-btn"
                                        onClick={() => applyRemove(project.id)}
                                        disabled={busy}
                                        title={`Remove ${project.count} file(s) from ${project.name}`}
                                    >
                                        <Chip size="sm" color={project.color} variant="neutral">
                                            {project.name} ({project.count})
                                        </Chip>
                                    </button>
                                ))}
                            </div>
                        )}
                        <p className="bulk-field-hint">
                            Removing only changes membership — the files stay in your workspace.
                        </p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default BulkProjectModal;
